import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Plane,
  Building,
  UtensilsCrossed,
  Handshake,
  Calendar as CalendarIcon,
  Car,
  Users,
  ArrowRight,
  X,
  AlertCircle,
  StickyNote,
  FolderOpen,
  BadgeCheck,
  Mic
} from 'lucide-react';

const ComponentTypeSelector = ({ onSelect, onCancel }) => {
  const [availableComponents, setAvailableComponents] = useState([]);
  const [urbanrideEnabled, setUrbanrideEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Original component type configurations (restored to pre-change state)
  const allComponentTypes = [
    {
      type: 'commercial_flight',
      title: 'Commercial Flight',
      description: 'Book or add commercial airline flights',
      icon: Plane,
      color: 'bg-ui-primary',
      lightColor: 'bg-ui-blue-bg',
      borderColor: 'border-ui-blue-line',
      gradient: 'from-ui-blue-bg to-ui-blue-bg',
      iconBg: 'bg-ui-primary'
    },
    {
      type: 'private_flight',
      title: 'Private Flight',
      description: 'Charter flights and private aviation',
      icon: Plane,
      color: 'bg-ui-violet-strong',
      lightColor: 'bg-ui-violet-bg',
      borderColor: 'border-ui-violet-line',
      gradient: 'from-ui-violet-bg to-ui-violet-bg',
      iconBg: 'bg-ui-violet-strong'
    },
    {
      type: 'hotel',
      title: 'Hotel',
      description: 'Book accommodations and lodging',
      icon: Building,
      color: 'bg-ui-green-strong',
      lightColor: 'bg-ui-green-bg',
      borderColor: 'border-ui-green-line',
      gradient: 'from-ui-green-bg to-ui-green-bg',
      iconBg: 'bg-ui-green-strong'
    },
    {
      type: 'restaurant',
      title: 'Restaurant',
      description: 'Dining reservations and meals',
      icon: UtensilsCrossed,
      color: 'bg-ui-orange-strong',
      lightColor: 'bg-ui-orange-bg',
      borderColor: 'border-ui-orange-line',
      gradient: 'from-ui-orange-bg to-ui-orange-bg',
      iconBg: 'bg-ui-orange-strong'
    },
    {
      type: 'meeting',
      title: 'Meeting',
      description: 'Business meetings and appointments',
      icon: Handshake,
      color: 'bg-ui-indigo-strong',
      lightColor: 'bg-ui-indigo-bg',
      borderColor: 'border-ui-indigo-line',
      gradient: 'from-ui-indigo-bg to-ui-indigo-bg',
      iconBg: 'bg-ui-indigo-strong'
    },
    {
      type: 'speaker_meeting',
      title: 'Speaker Meeting',
      description: 'Pharma & speaker-bureau speaking engagements',
      icon: Mic,
      color: 'bg-ui-red-strong',
      lightColor: 'bg-ui-red-bg',
      borderColor: 'border-ui-red-line',
      gradient: 'from-ui-red-bg to-ui-red-bg',
      iconBg: 'bg-ui-red-strong'
    },
    {
      type: 'event',
      title: 'Event',
      description: 'Conferences, seminars, and events',
      icon: CalendarIcon,
      color: 'bg-ui-pink-strong',
      lightColor: 'bg-ui-pink-bg',
      borderColor: 'border-ui-pink-line',
      gradient: 'from-ui-pink-bg to-ui-pink-bg',
      iconBg: 'bg-ui-pink-strong'
    },
    {
      type: 'rental_car',
      title: 'Rental Car',
      description: 'Vehicle rentals and transportation',
      icon: Car,
      color: 'bg-ui-teal-strong',
      lightColor: 'bg-ui-teal-bg',
      borderColor: 'border-ui-teal-line',
      gradient: 'from-ui-teal-bg to-ui-teal-bg',
      iconBg: 'bg-ui-teal-strong'
    },
    {
      type: 'chauffeur_service',
      title: 'Chauffeur Service',
      description: 'Private driver and car service',
      icon: Users,
      color: 'bg-ui-faint',
      lightColor: 'bg-ui-sunk',
      borderColor: 'border-ui-line',
      gradient: 'from-ui-sunk to-ui-sunk',
      iconBg: 'bg-ui-faint'
    },
    {
      type: 'airport_vip_service',
      title: 'Airport VIP Service',
      description: 'Meet & greet for an arrival or departure flight',
      icon: BadgeCheck,
      color: 'bg-ui-amber-strong',
      lightColor: 'bg-ui-amber-bg',
      borderColor: 'border-ui-amber-line',
      gradient: 'from-ui-amber-bg to-ui-amber-bg',
      iconBg: 'bg-ui-amber-strong'
    },
    {
      type: 'memo',
      title: 'Memo',
      description: 'Reminders, placeholders, and notes',
      icon: StickyNote,
      color: 'bg-ui-amber-strong',
      lightColor: 'bg-ui-amber-bg',
      borderColor: 'border-ui-amber-line',
      gradient: 'from-ui-amber-bg to-ui-amber-bg',
      iconBg: 'bg-ui-amber-strong'
    },
    {
      type: 'bundle',
      title: 'Bundle',
      description: 'Group related items together',
      icon: FolderOpen,
      color: 'bg-ui-violet-strong',
      lightColor: 'bg-ui-violet-bg',
      borderColor: 'border-ui-violet-line',
      gradient: 'from-ui-violet-bg to-ui-violet-bg',
      iconBg: 'bg-ui-violet-strong'
    }
  ];

  useEffect(() => {
    fetchAvailableComponents();
  }, []);

  const fetchAvailableComponents = async () => {
    try {
      setLoading(true);
      setError('');

      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Fetch available components and urbanride feature flag in parallel
      const [compResponse, featureResponse] = await Promise.allSettled([
        axios.get(`${apiBase}/organizations/available-components`, { headers }),
        axios.get(`${apiBase}/organizations/feature-check/urbanride_quickbook`, { headers })
      ]);

      if (compResponse.status === 'fulfilled') {
        setAvailableComponents(compResponse.value.data.available_components || []);
      } else {
        console.error('Error fetching available components:', compResponse.reason);
        setError('Failed to load available components');
        setAvailableComponents(allComponentTypes.map(ct => ct.type));
      }

      if (featureResponse.status === 'fulfilled') {
        setUrbanrideEnabled(featureResponse.value.data.enabled || false);
      }
    } catch (err) {
      console.error('Error fetching available components:', err);
      setError('Failed to load available components');
      setAvailableComponents(allComponentTypes.map(ct => ct.type));
    } finally {
      setLoading(false);
    }
  };

  // Filter component types based on what's available for the organization
  // Gate chauffeur_service on the urbanride_quickbook feature flag
  const componentTypes = allComponentTypes.filter(ct =>
    availableComponents.includes(ct.type) &&
    (ct.type !== 'chauffeur_service' || urbanrideEnabled)
  );

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        delay: index * 0.1,
        ease: "easeOut"
      }
    }),
    hover: {
      scale: 1.02,
      y: -2,
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-primary"></div>
          <span className="ml-3 text-ui-ink-3">Loading available components...</span>
        </div>
      )}

      {/* Error State */}  
      {error && !loading && (
        <div className="bg-ui-red-bg border border-ui-red-line rounded-md p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-ui-red-fg mr-2" />
            <span className="text-ui-red-fg">{error}</span>
          </div>
        </div>
      )}

      {/* No Components Available */}
      {!loading && !error && componentTypes.length === 0 && (
        <div className="text-center py-12">
          <div className="text-ui-muted text-6xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-ui-ink mb-2">No Components Available</h3>
          <p className="text-ui-ink-3">No component types are currently active for your organization.</p>
        </div>
      )}

      {/* Component Cards Grid */}
      {!loading && componentTypes.length > 0 && (
        <div className="component-type-grid grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
          {componentTypes.map((component, index) => {
            const Icon = component.icon;
            return (
              <motion.div
                key={component.type}
                custom={index}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                whileTap="tap"
                variants={cardVariants}
              >
                <div
                  className={`cursor-pointer transition-all duration-300 hover:shadow-2xl hover:shadow-${component.color}/20 border-2 ${component.borderColor} ${component.lightColor} group relative overflow-hidden h-full rounded-2xl`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelect(component.type);
                  }}
                  style={{ pointerEvents: 'auto' }}
                >
                  {/* Background Gradient Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${component.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} />
                  
                  <div className="p-6 h-full flex flex-col justify-between relative z-10 pointer-events-none">
                    <div className="flex-1">
                      {/* Icon Header */}
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-14 h-14 rounded-2xl ${component.iconBg} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
                          <Icon className="w-7 h-7 text-ui-on-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-ui-ink text-lg group-hover:text-ui-ink transition-colors">
                            {component.title}
                          </h3>
                        </div>
                      </div>
                      
                      {/* Description */}
                      <p className="text-ui-ink-3 leading-relaxed text-sm group-hover:text-ui-ink-2 transition-colors">
                        {component.description}
                      </p>
                    </div>
                    
                    {/* Action Arrow */}
                    <div className="flex justify-end mt-4">
                      <div className="w-8 h-8 rounded-full bg-ui-surface/50 flex items-center justify-center group-hover:bg-ui-surface group-hover:shadow-md transition-all duration-300">
                        <ArrowRight className="w-4 h-4 text-ui-ink-3 group-hover:text-ui-ink group-hover:translate-x-0.5 transition-all duration-300" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover Effect Border */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-ui-surface/30 transition-all duration-300 pointer-events-none" />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Menu Footer */}
      {!loading && (
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-ui-line px-6">
          <div className="text-sm text-ui-muted">
            {componentTypes.length > 0 
              ? 'Select a component type to continue'
              : 'No components available'
            }
          </div>
          <button
            onClick={onCancel}
            className="flex items-center gap-2 px-4 py-2 border border-ui-control rounded-lg hover:bg-ui-hover transition-colors"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ComponentTypeSelector;